import { PageLoad } from "@@/pages/page-load";
import { browserLogger } from "@@/settings";
import type { ParagraphContent } from "@@/shared/text";
import type {
    MILLISECONDS,
    Translatable,
    TranslatableValueFactoryParams,
} from "@towni/common";
import { asArray, toTranslatableString, translation } from "@towni/common";
import type { ErrorInfo, ReactNode } from "react";
import React, { Component } from "react";

const maxRetryCount = 200; // max number of retries
const errorDebounceMs = 1_000; // must be less than retryDelayMs
const exponentialBackoff = 1.1; // must be less than retryDelayMs
const minRetryDelayMs = 5_000; // min delay between retries
const maxRetryDelayMs = 60_000; // max delay between retries

interface WindowWithErrorRetryTracking extends Window {
    __alfredErrorRetryTracking?: Record<string, boolean>;
}

const windowWithErrorRetryTracking = window as WindowWithErrorRetryTracking;
if (!windowWithErrorRetryTracking.__alfredErrorRetryTracking) {
    windowWithErrorRetryTracking.__alfredErrorRetryTracking = {};
}

// Use sessionStorage to track if we've already gone through the retry cycle
const getStorageKey = (error: Error) => `retry_${error.name}_${error.message}`;
const hasReachedMaxRetries = (error: Error): boolean => {
    try {
        return (
            windowWithErrorRetryTracking.__alfredErrorRetryTracking?.[
                getStorageKey(error)
            ] === true
        );
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
        return false;
    }
};
const markMaxRetriesReached = (error: Error, value = true): void => {
    try {
        if (windowWithErrorRetryTracking.__alfredErrorRetryTracking) {
            windowWithErrorRetryTracking.__alfredErrorRetryTracking[
                getStorageKey(error)
            ] = value;
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
        // Retry storage errors
    }
};

type ErrorToRetry = {
    name: string | string[];
    message: string;
    retryingMessage?: ParagraphContent;
    errorMessageOverride?: Translatable | TranslatableValueFactoryParams;
};

type Props = {
    children: ReactNode;
    errorsToRetry: ErrorToRetry[];
    onRetry?: (error: Error) => void;
};

type State = {
    hasError: boolean;
    error?: Error;
    isRetrying: boolean;
    retryCount: number;
    currentErrorToRetry?: ErrorToRetry;
    lastErrorTime: number;
    lastRetryDelayMs: number;
};

class ErrorFilterBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: undefined,
        isRetrying: false,
        retryCount: 0,
        lastErrorTime: 0,
        lastRetryDelayMs: Math.floor(minRetryDelayMs / exponentialBackoff),
    };

    private retryTimeoutId?: NodeJS.Timeout;

    componentWillUnmount() {
        browserLogger.info(
            "ErrorFilterBoundary - unmounting, clearing all timeouts",
        );
        this.clearAllTimeouts();
    }

    clearAllTimeouts() {
        if (this.retryTimeoutId) {
            clearTimeout(this.retryTimeoutId);
            this.retryTimeoutId = undefined;
        }
    }

    componentDidCatch(error: Error, _errorInfo: ErrorInfo) {
        // You can also log the error to an error reporting service
        // browserLogger.warn(error, errorInfo);
        browserLogger.log("ErrorFilterBoundary caught error", error.name);

        // Do we care?
        const errorsToRetry = asArray(this.props.errorsToRetry);

        // If the error is the same as the one we want to retry
        const errorToRetry = errorsToRetry.find(errorToRetry => {
            if (Array.isArray(errorToRetry.name)) {
                return (
                    errorToRetry.name.includes(error.name) &&
                    errorToRetry.message === error.message
                );
            }
            return (
                errorToRetry.name === error.name &&
                errorToRetry.message === error.message
            );
        });

        const finalError = !errorToRetry?.errorMessageOverride
            ? error
            : new Error(
                  toTranslatableString(errorToRetry.errorMessageOverride),
                  { cause: error },
              );

        // Check if this error has already gone through the retry cycle
        if (hasReachedMaxRetries(finalError)) {
            browserLogger.warn(
                "This error has already gone through the retry cycle, throwing immediately",
            );
            this.setState({
                hasError: true,
                error: finalError,
            });
            return;
        }

        if (errorToRetry) {
            // Debounce error handling
            const now = Date.now();
            if (now - this.state.lastErrorTime < errorDebounceMs) {
                browserLogger.debug(
                    "Ignoring rapid consecutive error",
                    error.message,
                );
                return;
            }

            // Check if we already reached max retry count
            if (this.state.retryCount >= maxRetryCount) {
                // Mark that we're about to throw the final error
                // This prevents the component from retrying further if remounted
                browserLogger.warn("Max retry count reached, throwing error");

                // Store in sessionStorage that we've reached max retries for this error
                markMaxRetriesReached(finalError);

                this.setState({
                    retryCount: 0,
                    isRetrying: false,
                    currentErrorToRetry: undefined,
                    hasError: true,
                    error: finalError,
                    lastErrorTime: now,
                    lastRetryDelayMs: Math.round(
                        minRetryDelayMs / exponentialBackoff,
                    ),
                });
                return;
            }

            this.props.onRetry?.(error);

            // Start retry process
            const nextRetryCount = this.state.retryCount + 1;

            // Set timeout to retry after delay
            const nextRetryDelayMs = Math.min(
                Math.round(this.state.lastRetryDelayMs * exponentialBackoff),
                maxRetryDelayMs,
            );

            // Log exactly once per retry
            browserLogger.info(
                `Retry attempt ${nextRetryCount}; delay: ${nextRetryDelayMs}ms`,
                this.state.lastRetryDelayMs,
            );

            // Update state with new retry count
            this.setState({
                isRetrying: true,
                currentErrorToRetry: errorToRetry,
                retryCount: nextRetryCount,
                lastErrorTime: now,
                lastRetryDelayMs: nextRetryDelayMs,
            });

            this.clearAllTimeouts();
            this.retryTimeoutId = setTimeout(() => {
                this.setState({ isRetrying: false });
            }, nextRetryDelayMs);

            return;
        }

        // If error is not on the retry list, we need to catch it
        // and throw it again to be handled by the a real error boundary
        this.setState({
            hasError: true,
            error: finalError,
            lastErrorTime: Date.now(),
        });
    }

    static getDerivedStateFromError(error: Error) {
        browserLogger.warn("ErrorFilterBoundary - Error caught", error);
        // We don't want to render the ErrorPage as it is done by MainErrorBoundaries
        // return { hasError: true, error };
        return null;
    }

    render() {
        if (this.state.hasError) {
            // eslint-disable-next-line @typescript-eslint/only-throw-error
            throw this.state.error;
        }

        // If we're in retry mode, show loading screen but try to render children
        if (this.state.isRetrying) {
            return (
                <PageLoad
                    delayInMs={0 as MILLISECONDS}
                    text={
                        this.state.currentErrorToRetry?.retryingMessage ||
                        translation({
                            sv: "Ett problem har uppstått. Om felet kvarstår, pröva att ladda om sidan eller vänta och försöka igen senare.",
                            en: "An error has occurred. If the error persists, try reloading the page or wait and try again later.",
                        })
                    }
                />
            );
        }

        // If there is no error, render default children
        return (
            <React.Fragment key={this.state.retryCount}>
                {this.props.children}
            </React.Fragment>
        );
    }
}

export { ErrorFilterBoundary };
